<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-6">
      <h3 class="text-h5 font-weight-bold white--text mr-auto">{{ heading }}</h3>
      <v-btn color="#58d388" class="black--text font-weight-bold" @click="openDialog()" rounded>
        <v-icon left>mdi-plus</v-icon> Modul erstellen
      </v-btn>
    </div>

    <!-- Tier-Sektionen -->
    <div v-for="tier in managerGroups" :key="tier.key" class="tier-section mb-8">
      <div class="d-flex align-center mb-3">
        <v-icon :color="tier.color" class="mr-2">{{ tier.icon }}</v-icon>
        <h4 class="text-h6 font-weight-bold" :style="{ color: tier.color }">{{ tier.label }}</h4>
        <v-spacer />
        <span class="text-caption grey--text">{{ groupedModules[tier.key].length }} Module · Drag zum Sortieren</span>
      </div>

      <div v-if="groupedModules[tier.key].length === 0" class="empty-tier rounded-lg pa-6 text-center grey--text">
        Noch keine Module in dieser Stufe.
      </div>

      <draggable
        v-else
        :list="groupedModules[tier.key]"
        :animation="200"
        handle=".drag-handle"
        ghost-class="ghost-card"
        @end="onReorder(tier.key)"
        class="d-flex flex-wrap module-grid"
        item-key="_id">
        <div v-for="mod in groupedModules[tier.key]" :key="mod._id" class="module-tile">
          <v-card class="module-card rounded-xl pa-4 d-flex flex-column h-100" dark elevation="0">
            <div class="d-flex align-start mb-2">
              <v-icon class="drag-handle mr-2" color="grey">mdi-drag</v-icon>
              <v-icon :color="tier.color" class="mr-2">{{ mod.icon || "mdi-school" }}</v-icon>
              <div class="flex-grow-1">
                <div class="font-weight-bold text-truncate" style="max-width: 220px">{{ mod.title }}</div>
                <div class="text-caption grey--text">
                  {{ mod.lessons ? mod.lessons.length : 0 }} Lektionen · {{ mod.estimatedMinutes || 0 }} Min
                  <template v-if="isLeadership"> · {{ evaluationLabel(mod.evaluationType) }}</template>
                </div>
              </div>
              <v-chip :color="mod.active ? '#4caf50' : 'grey darken-3'" small class="ml-2">
                {{ mod.active ? "Aktiv" : "Inaktiv" }}
              </v-chip>
            </div>

            <div class="text-caption grey--text mb-3 description-clip">{{ mod.description || "—" }}</div>

            <v-spacer />

            <div class="d-flex align-center justify-end">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="yellow" v-bind="attrs" v-on="on" @click="openLessonEditor(mod)">
                    <v-icon small>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                </template>
                <span>Lektionen</span>
              </v-tooltip>
              <v-tooltip v-if="!isLeadership || mod.evaluationType !== 'video_submission'" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="orange" v-bind="attrs" v-on="on" @click="openQuizEditor(mod)">
                    <v-icon small>mdi-help-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Wissenstest</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="blue" v-bind="attrs" v-on="on" @click="openDialog(mod)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Bearbeiten</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="red" v-bind="attrs" v-on="on" @click="confirmDelete(mod)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Löschen</span>
              </v-tooltip>
            </div>
          </v-card>
        </div>
      </draggable>
    </div>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="800px" persistent dark scrollable>
      <v-card style="background-color: #1e1e1e">
        <v-card-title class="d-flex align-center">
          <span class="text-h5 gold-text">{{ editingId ? "Modul bearbeiten" : "Neues Modul" }}</span>
          <v-spacer />
          <v-btn icon :disabled="uploadingToolbox" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text style="max-height: 70vh">
          <v-form ref="form" v-model="valid">
            <v-row dense>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="form.title"
                  label="Titel"
                  :rules="[(v) => !!v || 'Titel ist erforderlich']"
                  required
                  outlined
                  dense></v-text-field>
              </v-col>
              <v-col v-if="!isLeadership" cols="12" md="4">
                <v-select
                  v-model="form.tier"
                  :items="['bronze', 'silber', 'gold']"
                  label="Stufe"
                  :rules="[(v) => !!v || 'Stufe erforderlich']"
                  outlined
                  dense
                  required></v-select>
              </v-col>
            </v-row>

            <template v-if="isLeadership">
              <v-select
                v-model="form.evaluationType"
                :items="evaluationOptions"
                item-text="label"
                item-value="value"
                label="Evaluation"
                outlined
                dense
                :rules="[(value) => !!value || 'Evaluation erforderlich']" />
              <v-textarea
                v-if="form.evaluationType === 'video_submission'"
                v-model="form.evaluationInstructions"
                label="Auftrag für die Video-Abgabe"
                hint="Diese Anleitung sehen Führungskräfte direkt vor der Video-Auswahl."
                persistent-hint
                outlined
                rows="3"
                class="mt-3" />
            </template>

            <v-textarea
              v-model="form.description"
              label="Kurzbeschreibung"
              hint="Wird auf der Modul-Karte und in der Übersicht angezeigt"
              outlined
              rows="2"
              dense
              persistent-hint></v-textarea>

            <v-textarea
              v-model="form.longDescription"
              label="Was lerne ich in diesem Modul? (ausführlich)"
              hint="Wird oberhalb der Lektionen angezeigt"
              outlined
              rows="4"
              dense
              class="mt-3"
              persistent-hint></v-textarea>

            <v-select v-model="form.icon" :items="iconOptions" label="Modul-Icon" outlined dense class="mt-3">
              <template v-slot:selection="{ item }">
                <v-icon left>{{ item }}</v-icon> {{ item }}
              </template>
              <template v-slot:item="{ item }">
                <v-icon left>{{ item }}</v-icon> {{ item }}
              </template>
            </v-select>

            <div class="auto-duration mb-3 d-flex align-center">
              <v-icon small color="grey" class="mr-2">mdi-clock-outline</v-icon>
              <span class="text-caption grey--text">Dauer wird automatisch aus den Lektionen berechnet</span>
            </div>

            <v-switch
              v-model="form.active"
              label="Aktiv (für Nutzer sichtbar)"
              color="#58d388"
              :hint="!editingId ? activationHint : ''"
              persistent-hint></v-switch>
          </v-form>

          <!-- TOOLBOX SECTION -->
          <v-divider class="my-6 border-white-05"></v-divider>
          <div class="d-flex align-center mb-3">
            <v-icon color="#58d388" class="mr-2">mdi-toolbox-outline</v-icon>
            <h4 class="text-h6">Toolbox</h4>
            <v-chip small class="ml-2" color="grey darken-3">{{ toolbox.length }}</v-chip>
          </div>

          <v-alert v-if="!editingId" type="info" outlined text dense class="rounded-lg mb-4">
            Speichere das Modul zuerst – danach kannst du Toolbox-Files hochladen.
          </v-alert>

          <template v-else>
            <v-row dense class="mb-1">
              <v-col cols="12" md="5">
                <v-file-input
                  v-model="newToolboxFile"
                  label="Datei (PDF, Bild, Video, Audio, …)"
                  outlined
                  dense
                  hide-details
                  :disabled="uploadingToolbox"
                  prepend-icon="mdi-paperclip"></v-file-input>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="newToolboxTitle"
                  label="Anzeige-Titel (optional)"
                  outlined
                  dense
                  hide-details
                  :disabled="uploadingToolbox"
                  prepend-icon="mdi-tag-outline"></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn
                  block
                  color="#58d388"
                  class="black--text"
                  :disabled="!newToolboxFile && !newToolboxText"
                  :loading="uploadingToolbox"
                  @click="uploadToolbox">
                  <v-icon left>mdi-upload</v-icon> Add
                </v-btn>
              </v-col>
            </v-row>

            <v-textarea
              v-model="newToolboxText"
              label="...oder einen Text / Notiz als Toolbox-Eintrag"
              outlined
              dense
              rows="2"
              :disabled="uploadingToolbox"
              class="mb-4"></v-textarea>

            <v-progress-linear v-if="uploadingToolbox" :value="toolboxProgress" color="#58d388" height="6" rounded class="mb-3"></v-progress-linear>

            <div v-if="toolbox.length === 0" class="grey--text text-caption text-center py-4">Noch keine Materialien hochgeladen.</div>

            <div v-else class="toolbox-grid">
              <v-card v-for="item in toolbox" :key="item._id" class="toolbox-item rounded-lg pa-3" dark elevation="0">
                <div class="d-flex align-start">
                  <div class="toolbox-icon mr-3" :class="`cat-${item.category}`">
                    <v-icon size="28" :color="categoryColor(item.category)">{{ categoryIcon(item.category) }}</v-icon>
                  </div>
                  <div class="flex-grow-1" style="min-width: 0">
                    <!-- Inline-Edit Titel -->
                    <div v-if="editingTitleId !== item._id" class="d-flex align-center">
                      <span class="font-weight-bold text-truncate flex-grow-1" :title="item.title || item.originalName">
                        {{ item.title || item.originalName }}
                      </span>
                      <v-btn icon small color="grey" class="ml-1" @click.stop="startEditTitle(item)">
                        <v-icon small>mdi-pencil-outline</v-icon>
                      </v-btn>
                    </div>
                    <div v-else class="d-flex align-center">
                      <v-text-field
                        v-model="editingTitleValue"
                        dense
                        hide-details
                        autofocus
                        outlined
                        color="#58d388"
                        style="font-size: 0.875rem"
                        @keydown.enter="saveTitle(item)"
                        @keydown.esc="cancelEditTitle"></v-text-field>
                      <v-btn icon small color="#58d388" class="ml-1" @click.stop="saveTitle(item)" :loading="savingTitle">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small color="grey" @click.stop="cancelEditTitle">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div class="text-caption grey--text">
                      <span class="text-uppercase">{{ item.category }}</span>
                      <template v-if="item.size"> · {{ formatSize(item.size) }}</template>
                    </div>
                  </div>
                  <v-btn icon small @click="previewItem(item)" v-if="item.category !== 'text'">
                    <v-icon small>mdi-eye-outline</v-icon>
                  </v-btn>
                  <v-btn icon small color="red" @click="deleteToolbox(item)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>

                <!-- Mini Preview -->
                <div v-if="item.category === 'image'" class="toolbox-preview mt-2">
                  <SecuredMedia :src="toolboxPath(item)" media-type="image" />
                </div>
                <div v-else-if="item.category === 'text'" class="text-caption grey--text mt-2 text-clip-3">
                  {{ item.textContent }}
                </div>
              </v-card>
            </div>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text :disabled="uploadingToolbox" @click="closeDialog">Schliessen</v-btn>
          <v-btn color="#58d388" class="black--text" @click="saveModule" :disabled="!valid || uploadingToolbox">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog.show" max-width="900" dark>
      <v-card v-if="previewDialog.item" style="background-color: #1e1e1e">
        <v-card-title>
          {{ previewDialog.item.title || previewDialog.item.originalName }}
          <v-spacer />
          <v-btn icon @click="previewDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <SecuredMedia
            :src="toolboxPath(previewDialog.item)"
            :media-type="previewDialog.item.category"
            :stream-playback="previewDialog.item.category === 'video'"
            :download-name="previewDialog.item.originalName"
            eager />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" top right>
      {{ snackbar.text }}
    </v-snackbar>

    <LessonEditor v-model="lessonDialog" :module="selectedModule" />
    <QuizEditor v-model="quizDialog" :module="selectedModule" />
  </div>
</template>

<script>
import api from "@/api"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"
import draggable from "vuedraggable"
import LessonEditor from "./LessonEditor.vue"
import QuizEditor from "./QuizEditor.vue"
import SecuredMedia from "../SecuredMedia.vue"

export default {
  name: "ModuleManager",
  components: { LessonEditor, QuizEditor, draggable, SecuredMedia },
  props: {
    track: { type: String, default: "license" },
    heading: { type: String, default: "Module Manager" },
  },
  data() {
    return {
      modules: [],
      loading: false,
      dialog: false,
      lessonDialog: false,
      quizDialog: false,
      valid: false,
      editingId: null,
      selectedModule: null,

      // Toolbox
      toolbox: [],
      newToolboxFile: null,
      toolboxUploadSession: null,
      toolboxUploadComplete: false,
      newToolboxText: "",
      newToolboxTitle: "",
      uploadingToolbox: false,
      toolboxProgress: 0,
      previewDialog: { show: false, item: null },
      editingTitleId: null,
      editingTitleValue: "",
      savingTitle: false,

      form: {
        title: "",
        description: "",
        longDescription: "",
        tier: "bronze",
        track: this.track,
        icon: "mdi-school",
        evaluationType: "quiz",
        evaluationInstructions: "",
        active: false,
      },

      iconOptions: [
        "mdi-school",
        "mdi-book-open-page-variant",
        "mdi-dumbbell",
        "mdi-heart-pulse",
        "mdi-brain",
        "mdi-chart-bar",
        "mdi-star",
        "mdi-certificate",
      ],

      tiers: [
        { key: "bronze", label: "Bronze", color: "#CD7F32", icon: "mdi-medal-outline" },
        { key: "silber", label: "Silber", color: "#C0C0C0", icon: "mdi-medal-outline" },
        { key: "gold", label: "Gold", color: "#FFD700", icon: "mdi-medal" },
      ],

      evaluationOptions: [
        { label: "Multiple-Choice-Wissenstest", value: "quiz" },
        { label: "Video-Abgabe mit manueller Prüfung", value: "video_submission" },
      ],

      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    isLeadership() {
      return this.track === "leadership"
    },
    managerGroups() {
      if (this.isLeadership) {
        return [{ key: "leadership", label: "Leadership Coaching", color: "#7ee5a6", icon: "mdi-account-tie-outline" }]
      }
      return this.tiers
    },
    activationHint() {
      return this.isLeadership
        ? "Aktiviere das Modul nach dem Erfassen aller Lektionen und der Evaluation."
        : "Aktivierung erst nach Quiz-Erstellung möglich"
    },
    groupedModules() {
      const groups = {}
      this.managerGroups.forEach((group) => {
        groups[group.key] = []
      })
      ;(this.modules || []).forEach((m) => {
        const matchesTrack = this.isLeadership ? m.track === "leadership" : !m.track || m.track === "license"
        if (!matchesTrack) return
        const groupKey = this.isLeadership ? "leadership" : m.tier
        if (groups[groupKey]) groups[groupKey].push(m)
      })
      Object.keys(groups).forEach((k) => groups[k].sort((a, b) => (a.order || 0) - (b.order || 0)))
      return groups
    },
  },
  mounted() {
    this.loadModules()
  },
  watch: {
    newToolboxFile() {
      const previousSession = this.toolboxUploadSession
      this.toolboxUploadSession = null
      this.toolboxUploadComplete = false
      if (previousSession) this.removePreparedToolboxMedia(previousSession)
    },
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    closeDialog() {
      if (this.uploadingToolbox) return
      const session = this.toolboxUploadSession
      this.toolboxUploadSession = null
      this.toolboxUploadComplete = false
      this.dialog = false
      if (session) this.removePreparedToolboxMedia(session)
    },
    removePreparedToolboxMedia(session) {
      if (!this.editingId || !session) return
      api.akademie.cancelModuleMediaUpload(this.editingId, "toolbox", session).catch((error) => {
        console.warn("Nicht verwendeter Toolbox-Upload konnte nicht entfernt werden", error)
      })
    },
    async loadModules() {
      this.loading = true
      try {
        this.modules = (await api.akademie.fetchModules({ track: this.track })) || []
      } catch (e) {
        console.error("Failed to load modules", e)
      } finally {
        this.loading = false
      }
    },
    async openDialog(item = null) {
      if (item) {
        this.editingId = item._id
        this.form = {
          title: item.title || "",
          description: item.description || "",
          longDescription: item.longDescription || "",
          tier: item.tier || "bronze",
          track: item.track || this.track,
          icon: item.icon || "mdi-school",
          evaluationType: item.evaluationType || (item.quiz ? "quiz" : "video_submission"),
          evaluationInstructions: item.evaluationInstructions || "",
          active: !!item.active,
        }
        this.toolbox = item.toolbox || []
        // Frische Daten holen (Toolbox)
        try {
          const fresh = await api.akademie.fetchModule(item._id)
          this.toolbox = fresh.toolbox || []
        } catch (_) {}
      } else {
        this.editingId = null
        this.form = {
          title: "",
          description: "",
          longDescription: "",
          tier: "bronze",
          track: this.track,
          icon: "mdi-school",
          evaluationType: "quiz",
          evaluationInstructions: "",
          active: false,
        }
        this.toolbox = []
      }
      this.newToolboxFile = null
      this.toolboxUploadSession = null
      this.toolboxUploadComplete = false
      this.newToolboxText = ""
      this.dialog = true
    },
    openLessonEditor(item) {
      this.selectedModule = item
      this.lessonDialog = true
    },
    openQuizEditor(item) {
      this.selectedModule = item
      this.quizDialog = true
    },
    async saveModule() {
      if (!this.$refs.form.validate()) return
      const isEdit = !!this.editingId
      try {
        if (isEdit) {
          await api.akademie.updateModule(this.editingId, this.form)
          await this.loadModules()
          this.notify("success", "Modul gespeichert")
          this.closeDialog()
        } else {
          const data = { ...this.form, active: false }
          const created = await api.akademie.createModule(data)
          if (created && created._id) {
            this.editingId = created._id
            this.notify("success", "Modul erstellt – jetzt Toolbox & Lektionen hinzufügen")
          }
          await this.loadModules()
          // Dialog bleibt offen, damit Toolbox/Lektionen hinzugefügt werden können
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || "Speichern fehlgeschlagen"
        this.notify("error", msg)
      }
    },
    async confirmDelete(item) {
      if (!confirm(`Möchten Sie das Modul "${item.title}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteModule(item._id)
        this.loadModules()
        this.notify("success", "Modul gelöscht")
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },

    async onReorder(tierKey) {
      const ids = this.groupedModules[tierKey].map((m) => m._id)
      try {
        await api.akademie.reorderModules(this.isLeadership ? "bronze" : tierKey, ids, this.track)
        // Local order sync
        this.groupedModules[tierKey].forEach((m, i) => (m.order = i))
        this.notify("success", "Reihenfolge gespeichert")
      } catch (e) {
        this.notify("error", "Sortierung konnte nicht gespeichert werden")
        this.loadModules()
      }
    },
    evaluationLabel(type) {
      return type === "video_submission" ? "Video-Abgabe" : "Wissenstest"
    },

    // --- Toolbox ---
    async uploadToolbox() {
      if (!this.editingId) return
      this.uploadingToolbox = true
      this.toolboxProgress = 0
      try {
        const payload = { title: this.newToolboxTitle || null }
        if (this.newToolboxFile) {
          if (this.newToolboxFile.size > 250 * 1024 * 1024) throw new Error("Datei max. 250MB")
          if (!this.toolboxUploadSession) {
            this.toolboxUploadSession = await api.akademie.prepareModuleMediaUpload(this.editingId, "toolbox", this.newToolboxFile)
          }
          if (!this.toolboxUploadComplete) {
            await uploadCloudflareMedia(this.newToolboxFile, this.toolboxUploadSession, {
              onProgress: (loaded, total) => {
                this.toolboxProgress = Math.round((loaded * 100) / Math.max(1, total || this.newToolboxFile.size))
              },
            })
            this.toolboxUploadComplete = true
          }
          payload.media = this.toolboxUploadSession
        } else {
          payload.textContent = this.newToolboxText
        }
        const item = await api.akademie.uploadToolboxItem(this.editingId, payload)
        this.toolbox.push(item)
        this.toolboxUploadSession = null
        this.toolboxUploadComplete = false
        this.newToolboxFile = null
        this.newToolboxText = ""
        this.newToolboxTitle = ""
        this.notify("success", "Eintrag hinzugefügt")
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.error) || e.message || "Upload fehlgeschlagen"
        this.notify("error", message)
      } finally {
        this.uploadingToolbox = false
      }
    },
    startEditTitle(item) {
      this.editingTitleId = item._id
      this.editingTitleValue = item.title || item.originalName || ""
    },
    cancelEditTitle() {
      this.editingTitleId = null
      this.editingTitleValue = ""
    },
    async saveTitle(item) {
      this.savingTitle = true
      try {
        const updated = await api.akademie.updateToolboxItem(this.editingId, item._id, { title: this.editingTitleValue })
        item.title = updated.title
        this.cancelEditTitle()
        this.notify("success", "Titel gespeichert")
      } catch (e) {
        this.notify("error", "Speichern fehlgeschlagen")
      } finally {
        this.savingTitle = false
      }
    },
    async deleteToolbox(item) {
      if (!confirm(`"${item.title || item.originalName}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteToolboxItem(this.editingId, item._id)
        this.toolbox = this.toolbox.filter((t) => t._id !== item._id)
        this.notify("success", "Eintrag gelöscht")
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },
    toolboxPath(item) {
      return item.category === "video"
        ? api.akademie.getToolboxItemPlaybackPath(this.editingId, item._id)
        : api.akademie.getToolboxItemPath(this.editingId, item._id)
    },
    previewItem(item) {
      this.previewDialog = { show: true, item }
    },
    categoryIcon(cat) {
      const map = {
        image: "mdi-image-outline",
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        pdf: "mdi-file-pdf-box",
        text: "mdi-text-box-outline",
        file: "mdi-file-outline",
      }
      return map[cat] || "mdi-file-outline"
    },
    categoryColor(cat) {
      const map = {
        image: "#42a5f5",
        video: "#ef5350",
        audio: "#ab47bc",
        pdf: "#e53935",
        text: "#66bb6a",
        file: "#bdbdbd",
      }
      return map[cat] || "#bdbdbd"
    },
    formatSize(b) {
      if (!b) return ""
      if (b < 1024) return b + " B"
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB"
      return (b / 1024 / 1024).toFixed(1) + " MB"
    },
  },
}
</script>

<style scoped>
.gold-text {
  background: linear-gradient(45deg, #58d388, #7ee5a6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tier-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  padding: 16px 16px 8px;
}

.module-grid {
  gap: 16px;
}

.module-tile {
  flex: 1 1 280px;
  max-width: 340px;
  min-width: 240px;
}

.module-card {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: transform 0.2s, border-color 0.2s;
}
.module-card:hover {
  border-color: rgba(0, 150, 64, 0.4);
  transform: translateY(-2px);
}
.drag-handle {
  cursor: grab;
}
.drag-handle:active {
  cursor: grabbing;
}
.ghost-card {
  opacity: 0.4;
  background: rgba(0, 150, 64, 0.1) !important;
}
.empty-tier {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.description-clip {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-clip-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.h-100 {
  height: 100%;
}
.gap-3 {
  gap: 12px;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.toolbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.toolbox-item {
  background: rgba(20, 20, 20, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.toolbox-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
}

.toolbox-preview img {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 8px;
}
.auto-duration {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 8px 12px;
}
</style>
